import { Autocomplete, TextField } from "@mui/material";
import { useMemo } from "react";
import { useController, UseFormReturn } from "react-hook-form";
import { AutocompleteActionsContext, AutocompleteActionsPaper } from './autocomplete_actions_paper';
import { useVirtualizedAutocomplete } from './use_virtualized_autocomplete';
import { VirtualizedAutocompleteContext, VirtualizedAutocompleteListbox } from './virtualized_autocomplete_listbox';

type Props<T, F extends {}> = {
  name: keyof F;
  label?: string;
  newButtonLabel?: string;
  value?: T | null;
  options: T[];
  required?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  fullWidth?: boolean;
  reactHookForm: UseFormReturn<F>;
  multiLineRows?: number;
  getOptionLabel: (opt: T) => string;
  getOptionKey: (opt: T) => string | number;
  onNew?: () => void;
  onChangeValue?: (id: string | number | null) => void;
  getOptionDisabled?: (opt: T) => boolean;
};

export const DefaultFormAutocomplete = <T, F extends {},>({
  name, label, newButtonLabel, options, required, readOnly, placeholder,
  fullWidth, reactHookForm, multiLineRows, getOptionLabel, getOptionKey,
  onNew, onChangeValue, getOptionDisabled,
}: Props<T, F>) => {
  const { field, fieldState, formState } = useController({ name: name as any, control: reactHookForm.control });
  const selectedOption = useMemo(
    () => options.find(option => getOptionKey(option) === field.value) ?? null,
    [options, getOptionKey, field.value]
  );
  const isMultiline = multiLineRows !== undefined && multiLineRows > 1;
  // Options already supported pre-line text, independently of the input rows.
  const { contextValue, ...virtualizationProps } = useVirtualizedAutocomplete(options, getOptionLabel, true);
  const actions = useMemo(() => ({ onNew, newButtonLabel, disabled: Boolean(readOnly || formState.disabled) }),
    [onNew, newButtonLabel, readOnly, formState.disabled]);
  const helperText = typeof fieldState.error?.message === 'string' ? fieldState.error.message : null;

  return (
    <AutocompleteActionsContext.Provider value={actions}>
      <VirtualizedAutocompleteContext.Provider value={contextValue}>
        <Autocomplete
          {...virtualizationProps}
          disableListWrap
          size="small"
          fullWidth={fullWidth}
          getOptionLabel={getOptionLabel}
          getOptionKey={getOptionKey}
          options={options}
          slots={{ paper: AutocompleteActionsPaper }}
          slotProps={{ listbox: { component: VirtualizedAutocompleteListbox } }}
          sx={{ minHeight: 40 }}
          readOnly={readOnly}
          aria-multiline={isMultiline}
          disabled={formState.disabled}
          value={selectedOption}
          getOptionDisabled={getOptionDisabled}
          onBlur={field.onBlur}
          onChange={(_, newValue) => {
            const id = newValue === null ? null : getOptionKey(newValue);
            field.onChange(id);
            onChangeValue?.(id);
          }}
          renderInput={params => (
            <TextField
              {...params}
              inputRef={field.ref}
              size="small"
              label={label}
              error={helperText !== null}
              helperText={helperText}
              placeholder={placeholder}
              required={required}
              multiline={isMultiline}
              rows={multiLineRows}
              sx={{
                '& .MuiInputBase-input': { whiteSpace: 'pre-wrap' },
                '& textarea.MuiInputBase-input': { whiteSpace: 'pre-wrap' },
              }}
            />
          )}
        />
      </VirtualizedAutocompleteContext.Provider>
    </AutocompleteActionsContext.Provider>
  );
};
